import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useDischargeContext } from '~/hooks/discharge/useDischargeContext';
import NinetyDaySummaryShell from '~/components/patient-board/NinetyDaySummaryShell';
import PatientBoardHeader from '~/components/patient-board/PatientBoardHeader';
import PatientPhaseAccordion from '~/components/patient-board/PatientPhaseAccordion';
import DischargeNinetyDaySummary from '~/components/discharge/patient-detail/DischargeNinetyDaySummary';
import { buildNinetyDaySummary } from '~/components/discharge/patient-detail/buildNinetyDaySummary';
import ProgramFitList from '~/components/discharge/patient-detail/ProgramFitList';
import PlaceSection from '~/components/discharge/patient-detail/PlaceSection';
import ReferSection from '~/components/discharge/patient-detail/ReferSection';
import MonitorSection from '~/components/discharge/patient-detail/MonitorSection';
import FollowUpSection from '~/components/discharge/patient-detail/FollowUpSection';
import { DISPOSITION_LABELS, PHASE_ORDER } from '~/types/discharge';
import { getSelectedProviderName } from '~/components/discharge/patient-detail/buildPlaceMatches';
import { mapDischargePatient, makeDischargeConfig, DISCHARGE_PHASES } from './dischargeToVM';

const DischargePatientDetail: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const { getPatientById, setDisposition, setPhase, setSelectedProvider, takeOwnership } = useDischargeContext();

  const patient = patientId ? getPatientById(patientId) : undefined;
  const config = useMemo(() => makeDischargeConfig(takeOwnership), [takeOwnership]);

  if (!patient) {
    return (
      <Box sx={{ mt: 1.5, mb: 4, px: { xs: 2, sm: 3, md: 4 } }}>
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/discharge/all-patients')} sx={{ mb: 2 }}>
          Back to patients
        </Button>
        <Typography color="text.secondary">Patient not found.</Typography>
      </Box>
    );
  }

  const vm = mapDischargePatient(patient);

  // Referral-warning pill (shown in the header until the patient is past Refer).
  const pastRefer = PHASE_ORDER.indexOf(patient.phase) > PHASE_ORDER.indexOf('refer');
  const fallbackName = getSelectedProviderName(patient) ?? undefined;
  const noResponseName = !pastRefer
    ? (patient.noResponseProviderName ?? (patient.referralResponse === 'no_response' ? fallbackName : undefined))
    : undefined;
  const declinedName = !pastRefer
    ? (patient.declinedProviderName ?? (patient.referralResponse === 'declined' ? fallbackName : undefined))
    : undefined;
  const referralWarning =
    noResponseName ? `No response from ${noResponseName}`
    : declinedName ? `${declinedName} declined the patient`
    : null;

  const headerBadges = referralWarning ? (
    <Tooltip title={referralWarning} arrow>
      <Box
        sx={{
          width: 20, height: 20, borderRadius: '50%',
          bgcolor: '#fff4e5',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <WarningAmberIcon sx={{ fontSize: 14, color: '#ed6c02' }} />
      </Box>
    </Tooltip>
  ) : undefined;

  const subtext: Record<string, React.ReactNode> = (() => {
    const currentIdx = PHASE_ORDER.indexOf(patient.phase);
    const s: Record<string, React.ReactNode> = {};
    if (currentIdx > PHASE_ORDER.indexOf('predict')) {
      s.predict = <>Expected disposition: <strong>{DISPOSITION_LABELS[patient.disposition]}</strong></>;
    }
    if (currentIdx > PHASE_ORDER.indexOf('place')) {
      const providerName = getSelectedProviderName(patient);
      if (providerName) {
        s.place = <>Selected Provider: <strong>{providerName}</strong></>;
      }
    }
    if (currentIdx > PHASE_ORDER.indexOf('refer')) {
      s.refer = 'Referral accepted';
    } else if (patient.referralResponse === 'no_response' || patient.noResponseProviderName) {
      const name = patient.noResponseProviderName ?? patient.selectedProviderName;
      s.refer = name ? <>No response from <strong>{name}</strong></> : 'No response received';
    } else if (patient.declinedProviderName) {
      s.refer = <><strong>{patient.declinedProviderName}</strong> declined the patient</>;
    }
    if (currentIdx > PHASE_ORDER.indexOf('discharge')) {
      s.discharge = 'Discharge order completed';
    }
    return s;
  })();

  return (
    <Box sx={{ mt: 1.5, mb: 4, px: { xs: 2, sm: 3, md: 4 } }}>
      <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/discharge/all-patients')} sx={{ mb: 2 }}>
        Back to patients
      </Button>

      <PatientBoardHeader patient={vm} config={config} headerBadges={headerBadges} />

      <NinetyDaySummaryShell>
        <DischargeNinetyDaySummary summary={buildNinetyDaySummary(patient)} />
      </NinetyDaySummaryShell>

      <PatientPhaseAccordion
        phases={DISCHARGE_PHASES}
        currentPhase={patient.phase}
        syncKey={`${patient.phase}-${patient.disposition}`}
        warningPhase={
          PHASE_ORDER.indexOf(patient.phase) <= PHASE_ORDER.indexOf('refer') &&
          (patient.noResponseProviderName || patient.referralResponse === 'no_response' || patient.declinedProviderName)
            ? 'refer'
            : undefined
        }
        content={{
          predict: (
            <ProgramFitList
              patient={patient}
              selectedDisposition={
                PHASE_ORDER.indexOf(patient.phase) > PHASE_ORDER.indexOf('predict') ? patient.disposition : null
              }
              onSelect={(d) => {
                setDisposition(patient.id, d);
                setPhase(patient.id, 'place');
                setSelectedProvider(patient.id, undefined);
              }}
            />
          ),
          place: <PlaceSection key={patient.disposition} patient={patient} />,
          refer: <ReferSection patient={patient} />,
          monitor: <MonitorSection patient={patient} />,
          followup: <FollowUpSection patient={patient} />,
        }}
        subtext={subtext}
      />
    </Box>
  );
};

export default DischargePatientDetail;
